//@ts-ignore
import useResizeAware from "react-resize-aware";

import CardStatusIcon from "@Components/game/cards/card-status-icon";
import { CardLeftGradient } from "@Components/game/cards/styled-components";
import { faCheck, faClose, faLockAlt } from "@fortawesome/pro-solid-svg-icons";
import useChallengeCardState from "@hooks/game/use-challenge-card-state";
import useI18n from "@i18n";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { ChallengeResponse } from "@Shared/schemas/challenges/challengeSchemas";
import { useTranslation } from "react-i18next";
import { ChallengeCardBlur, ChallengeCardLargeContentWrapper, ChallengeCardLargeLeftContent, ChallengeCardLargeRoot } from "./styled-components";

interface IChallengeCardLarge {
  challenge: ChallengeResponse;
  disabled?: boolean;
  caption?: string;
}

/**
 *
 * @param challenge - A Challenge object
 * @param disabled - make it impossible for the user to navigate to the Challenge
 * @param caption - Optional caption to be shown instead of League and Division
 *
 * @returns A large Challenge card indended to fill the whole width of the screen (minus padding/gutters)
 */
const ChallengeCardLarge = ({ challenge, disabled, caption }: IChallengeCardLarge) => {
  const { i18n } = useI18n();
  const { t } = useTranslation("VR");
  const theme = useTheme();
  const [resizeListener, sizes] = useResizeAware();
  
  const { isDisabled, isCompleted, isFailed, league, division, locizeKeyPrefix, handleNavigate } = useChallengeCardState({ challenge, disabled });

  return (
    <>
      {resizeListener}
      <Box sx={{ cornerShape: "squircle", borderRadius: "48px", height: sizes?.height, overflow: "hidden", position: "relative" }}>
        <ChallengeCardLargeRoot imageUrl={challenge.image_url}>
          {isDisabled && <ChallengeCardBlur />}

          {(isDisabled || isCompleted || isFailed) && (
            <CardStatusIcon
              icon={isDisabled ? faLockAlt : isCompleted ? faCheck : faClose}
              color={!isDisabled ? theme.palette[isCompleted ? "dsSuccess" : "dsDestructive"].v300 : undefined}
              size={92}
              iconSize="2xl"
            />
          )}

          <ChallengeCardLargeContentWrapper sx={{ opacity: isDisabled ? 0.5 : 1 }}>
            <ChallengeCardLargeLeftContent>
              <Typography variant="text1xl500" color="dsNeutral.v200" sx={{ marginBottom: theme.spacing(10), textDecoration: "truncate" }}>
                {caption || `${i18n(`league.${league}.short`)} · ${i18n("division.specific", { division: division })}`}
              </Typography>
              <Typography variant="text7xl700" color="common.white" sx={{ marginBottom: theme.spacing(2) }}>
                {t(`${locizeKeyPrefix}.name`, `${challenge.title}`)}
              </Typography>
              <Typography variant="textLg500" color="common.white" sx={{ marginBottom: theme.spacing(7) }}>
                {t(`${locizeKeyPrefix}.goal`, `${challenge.goal}`)}
              </Typography>
              <Box sx={{ marginBottom: theme.spacing(11) }}>
                <Button color="info" onClick={handleNavigate} disabled={isDisabled}>
                  {i18n("challenges.play-challenge")}
                </Button>
              </Box>
            </ChallengeCardLargeLeftContent>
          </ChallengeCardLargeContentWrapper>
          <CardLeftGradient />
        </ChallengeCardLargeRoot>
      </Box>
    </>
  );
};

export default ChallengeCardLarge;
